import React, { useState } from 'react';
import { 
  TextInput, 
  PasswordInput, 
  Button, 
  Paper, 
  Title, 
  Container, 
  Text, 
  Anchor,
  Stack,
  Alert,
  Checkbox
} from '@mantine/core';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

/**
 * Login page component
 * Provides a form for users to log in with email and password
 */
const Login: React.FC = () => {
  // State to store form values
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Track whether the user wants their session persisted across browser restarts
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Get auth functions from context
  const { login, loading, error } = useAuth();

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    try {
      // Call login function from auth context
      await login(email, password, stayLoggedIn);
      // Redirect is handled in the auth context
    } catch (err: any) {
      // Set form error if login fails
      setFormError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    }
  };

  return (
    <Layout hideNavigation>
      <Container size={420} my={40}>
        <Title ta="center">Welcome back!</Title>
        <Text c="dimmed" size="sm" ta="center" mt={5}>
          Don't have an account yet?{' '}
          <Anchor component={Link} to="/register" size="sm">
            Create account
          </Anchor>
        </Text>

        <Paper withBorder shadow="md" p={30} mt={30} radius="md">
          <form onSubmit={handleSubmit}>
            <Stack>
              {/* Show error message if login fails */}
              {(formError || error) && (
                <Alert color="red" title="Error">
                  {formError || error}
                </Alert>
              )}
              
              <TextInput
                label="Email"
                placeholder="your@email.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <PasswordInput
                label="Password"
                placeholder="Your password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Checkbox
                label="Stay logged in"
                checked={stayLoggedIn}
                onChange={(e) => setStayLoggedIn(e.currentTarget.checked)}
              />
              <Button type="submit" loading={loading} fullWidth mt="xl">
                Sign in
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Layout>
  );
};

export default Login; 